import React, { useState, useEffect, useRef, useMemo } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, Circle, LayersControl, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { supabase } from '../config/supabase';
import FilterPanel from '../components/FilterPanel';
import NoEventsMessage from '../components/NoEventsMessage';
import NoLocationMessage from '../components/NoLocationMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import { useEventFilters } from '../hooks/useEventFilters';
import { useFilteredEvents } from '../hooks/useFilteredEvents';
import { 
  CultureIcon, 
  ArtIcon, 
  MusicIcon, 
  TheaterIcon, 
  CinemaIcon, 
  FestivalIcon, 
  SportIcon, 
  WorkshopIcon, 
  MarketIcon, 
  ConferenceIcon, 
  NatureIcon, 
  FamilyIcon,
  SearchIcon 
} from '../components/icons/NavIcons';

// Fonction pour obtenir le contenu SVG d'une catégorie
const getCategorySvgContent = (categoryName) => {
  // Normaliser le nom de catégorie (enlever accents, majuscules)
  const normalize = (str) => str
    ?.normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
  
  const normalizedName = normalize(categoryName);
  
  const iconMap = {
    'culture': CultureIcon,
    'art': ArtIcon,
    'music': MusicIcon,
    'theater': TheaterIcon,
    'cinema': CinemaIcon,
    'festival': FestivalIcon,
    'sport': SportIcon,
    'atelier': WorkshopIcon,
    'workshop': WorkshopIcon,
    'market': MarketIcon,
    'conference': ConferenceIcon,
    'nature': NatureIcon,
    'family': FamilyIcon
  };
  
  const IconComponent = iconMap[normalizedName] || CultureIcon;
  
  // Générer le SVG et extraire son contenu interne
  const svgString = renderToStaticMarkup(
    <IconComponent 
      style={{ 
        stroke: 'white', 
        fill: 'none', 
        strokeWidth: 2.5, 
        strokeLinecap: 'round', 
        strokeLinejoin: 'round' 
      }} 
    />
  );
  
  const match = svgString.match(/<svg[^>]*>([\s\S]*)<\/svg>/);
  return match ? match[1] : '';
};

// Fonction pour créer un marker personnalisé rond avec pointe
const createCustomMarker = (color, categoryName, isToday = false, isBoosted = false, title = '', showTitle = false) => {
  const size = isBoosted ? 48 : 48;
  const iconSize = isBoosted ? 20 : 20;
  
  const markerColor = color || 'rgb(40, 148, 216)';
  const iconSvgContent = getCategorySvgContent(categoryName);
  
  // Fonction pour éclaircir une couleur
  const lightenColor = (color, percent) => {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, (num >> 16) + amt);
    const G = Math.min(255, ((num >> 8) & 0x00FF) + amt);
    const B = Math.min(255, (num & 0x0000FF) + amt);
    return `rgb(${R}, ${G}, ${B})`;
  };
  
  // Convertir la couleur en format hex si nécessaire et créer la version claire
  let lightColor, darkColor;
  if (markerColor.startsWith('rgb')) {
    const match = markerColor.match(/\d+/g);
    if (match) {
      const [r, g, b] = match.map(Number);
      const hex = '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
      lightColor = lightenColor(hex, 30);
      darkColor = markerColor;
    }
  } else {
    lightColor = lightenColor(markerColor, 30);
    darkColor = markerColor;
  }
  
  // Générer un ID unique pour les filtres SVG
  const uniqueId = `marker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  const svgMarker = `
    <div style="position: relative; width: ${size}px; height: ${size + (showTitle ? 28 : 8)}px;">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 56" width="${size}" height="${size + 8}" style="display: block; ${isToday ? 'animation: pulse-marker 1s ease-in-out infinite;' : ''}">
        <defs>
          <filter id="shadow-${uniqueId}" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
          </filter>
          <linearGradient id="gradient-${uniqueId}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:${lightColor}" />
            <stop offset="100%" style="stop-color:${darkColor}" />
          </linearGradient>
          ${isToday ? `
          <style>
            @keyframes pulse-marker {
              0%, 100% { 
                transform: scale(1);
                opacity: 1;
              }
              50% { 
                transform: scale(1.1);
                opacity: 1;
              }
            }
          </style>
          ` : ''}
          ${isBoosted ? `
          <style>
            @keyframes pulse-halo {
              0% { 
                opacity: 1;
                transform: scale(0.9);
                stroke-width: 3;
              }
              100% { 
                opacity: 0.1;
                transform: scale(1.5);
                stroke-width: 1;
              }
            }
          </style>
          ` : ''}
        </defs>
        
        ${isBoosted ? `
        <!-- Anneau jaune pour les événements boostés -->
        <circle cx="24" cy="24" r="24" fill="none" stroke="#1783dbff" stroke-width="3" style="${isBoosted ? 'animation: pulse-halo 0.8s ease-out infinite; transform-origin: center center;' : ''}"/>
        ` : ''}
        
        <!-- Cercle principal avec bordure blanche et dégradé -->
        <circle cx="24" cy="24" r="20" fill="url(#gradient-${uniqueId})" stroke="white" stroke-width="4" filter="url(#shadow-${uniqueId})"/>
        
        <!-- Pointe vers le bas -->
        <path d="M 20 44 L 30 44 L 25 52 Z" fill="white" stroke="white" stroke-width="1"/>
        
        <!-- Icône de catégorie intégrée et centrée -->
        <g transform="translate(12, 12)">
          <svg viewBox="0 0 24 24" width="${iconSize}" height="${iconSize}" x="2" y="2">
            <g fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              ${iconSvgContent}
            </g>
          </svg>
        </g>
      </svg>
      ${showTitle ? `
      <div style="position: absolute; top: ${size + 8}px; left: 50%; transform: translateX(-50%); white-space: nowrap; padding: 2px 6px; font-size: 15px; font-weight: 600; color: ${darkColor}; text-shadow: -2px -2px 0 white, 2px -2px 0 white, -2px 2px 0 white, 2px 2px 0 white, -2px 0 0 white, 2px 0 0 white, 0 -2px 0 white, 0 2px 0 white, -3px -3px 0 white, 3px -3px 0 white, -3px 3px 0 white, 3px 3px 0 white, -3px 0 0 white, 3px 0 0 white, 0 -3px 0 white, 0 3px 0 white; max-width: 200px; overflow: hidden; text-overflow: ellipsis;">
        ${title}
      </div>
      ` : ''}
    </div>
  `;
  
  return L.divIcon({
    html: svgMarker,
    className: 'custom-marker-round',
    iconSize: [size, size + (showTitle ? 28 : 8)],
    iconAnchor: [size / 2, size + 8],
    popupAnchor: [0, -(size + 8)]
  });
};

// Icône pour la position de l'utilisateur (point bleu avec contour blanc et pulsation)
const createUserPositionIcon = () => {
  const svgIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
      <defs>
        <style>
          @keyframes pulse-user {
            0%, 100% { 
              opacity: 0.3;
              transform: scale(1);
            }
            50% { 
              opacity: 0.1;
              transform: scale(2);
            }
          }
        </style>
      </defs>
      <!-- Cercle de pulsation externe -->
      <circle cx="16" cy="16" r="12" fill="#2894D8" opacity="0.3" style="animation: pulse-user 2s ease-in-out infinite; transform-origin: center;"/>
      <!-- Point central -->
      <circle cx="16" cy="16" r="8" fill="#2894D8" stroke="#fff" stroke-width="3"/>
    </svg>
  `;
  
  return L.divIcon({
    html: svgIcon,
    className: 'user-position-marker',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16]
  });
};

// Composant pour gérer les événements de la carte
const MapEventsHandler = ({ onMapMove }) => {
  const map = useMapEvents({
    moveend: () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      onMapMove({ center: [center.lat, center.lng], zoom });
    },
    zoomend: () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      onMapMove({ center: [center.lat, center.lng], zoom });
    }
  });
  return null;
};

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const eventsFetchedRef = useRef(false);
  const categoriesFetchedRef = useRef(false);
  const ageGroupsFetchedRef = useRef(false);
  const mapRef = useRef(null);

  const [showFilters, setShowFilters] = useState(false);
  
  const [filterDate, setFilterDate] = useState(() => {
    const saved = localStorage.getItem('filterDate');
    if (saved) return saved;
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  
  const [filterEndDate, setFilterEndDate] = useState(() => {
    const saved = localStorage.getItem('filterEndDate');
    if (saved) return saved;
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  });
  
  const [filterLocation, setFilterLocation] = useState(() => 
    localStorage.getItem('filterLocation') || ''
  );
  
  const [communeName, setCommuneName] = useState(() => 
    localStorage.getItem('communeName') || ''
  );
  
  const [communeInseeCode, setCommuneInseeCode] = useState(() => 
    localStorage.getItem('communeInseeCode') || ''
  );
  
  const [searchPosition, setSearchPosition] = useState(() => {
    const saved = localStorage.getItem('searchPosition');
    return saved ? JSON.parse(saved) : null;
  });
  
  // Ajouter l'état pour la position GPS locale (non sauvegardée)
  const [localGPSPosition, setLocalGPSPosition] = useState(null);
  
  // Fonction callback pour recevoir la position GPS
  const handleLocalGPSPositionUpdate = (position) => {
    setLocalGPSPosition(position);
    console.log('[HomePage] Position GPS mise à jour:', position);
  };

  const [filterDistance, setFilterDistance] = useState(() => {
    const saved = localStorage.getItem('filterDistance');
    return saved ? Number(saved) : 3;
  });
  
  const [nearbyPostalCodes, setNearbyPostalCodes] = useState([]);
  const [nearbyCommuneInsee, setNearbyCommuneInsee] = useState([]);
  const [categories, setCategories] = useState([]);
  
  const [filterCategories, setFilterCategories] = useState(() => {
    const saved = localStorage.getItem('filterCategories');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [ageGroups, setAgeGroups] = useState([]);
  
  const [filterAgeGroup, setFilterAgeGroup] = useState(() => 
    localStorage.getItem('filterAgeGroup') || ''
  );
  
  const [filterAllWeather, setFilterAllWeather] = useState(() => 
    localStorage.getItem('filterAllWeather') === 'true'
  );
  
  const [filterAccessible, setFilterAccessible] = useState(() => 
    localStorage.getItem('filterAccessible') === 'true'
  );
  
  const [filterFree, setFilterFree] = useState(() => 
    localStorage.getItem('filterFree') === 'true'
  );

  const [isFilteringComplete, setIsFilteringComplete] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);

  const [tileProvider, setTileProvider] = useState('osm');

  const [mapCenter, setMapCenter] = useState(() => {
    // ORDRE DE PRIORITÉ: 1) mapCenter sauvegardé (déplacement manuel), 2) searchPosition (centroïde commune), 3) défaut France
    const savedCenter = localStorage.getItem('mapCenter');
    if (savedCenter) {
      try {
        const center = JSON.parse(savedCenter);
        console.log('[MapCenter] Centre chargé depuis localStorage (priorité 1):', center);
        return center;
      } catch (e) {
        console.error('[MapCenter] Erreur lors du chargement de mapCenter:', e);
      }
    }
    
    const savedPosition = localStorage.getItem('searchPosition');
    if (savedPosition) {
      try {
        const pos = JSON.parse(savedPosition);
        const center = [pos.latitude, pos.longitude];
        console.log('[MapCenter] Initialisation avec searchPosition (priorité 2):', center);
        return center;
      } catch (e) {
        console.error('[MapCenter] Erreur lors du chargement de searchPosition:', e);
      }
    }
    
    // Centre par défaut : France métropolitaine (priorité 3)
    console.log('[MapCenter] Utilisation du centre par défaut (France)');
    return [46.603354, 1.888334];
  });

  const [mapZoom, setMapZoom] = useState(() => {
    // ORDRE DE PRIORITÉ: 1) mapZoom sauvegardé (zoom manuel), 2) searchPosition → 12, 3) défaut 6
    const savedZoom = localStorage.getItem('mapZoom');
    if (savedZoom) {
      console.log('[MapZoom] Zoom chargé depuis localStorage (priorité 1):', savedZoom);
      return parseInt(savedZoom);
    }
    
    const savedPosition = localStorage.getItem('searchPosition');
    if (savedPosition) {
      console.log('[MapZoom] Initialisation avec searchPosition (priorité 2): 12');
      return 12;
    }
    
    // Zoom par défaut pour la France (priorité 3)
    console.log('[MapZoom] Utilisation du zoom par défaut (France): 6');
    return 6;
  });
  
  const [currentZoom, setCurrentZoom] = useState(mapZoom);
  
  // Ref pour détecter le premier chargement
  const isFirstMount = useRef(true);
  
  // Ref pour stocker filterLocation et détecter les changements
  const cachedFilterLocation = useRef(filterLocation);
  
  // Ref pour stocker la dernière searchPosition utilisée pour le recentrage
  const lastSearchPositionRef = useRef(searchPosition);

  // Remplacer l'ancien useEffect par le hook
  const { events, loading, error } = useFilteredEvents({
    searchPosition,
    nearbyPostalCodes,
    filterDate,
    filterEndDate,
    filterCategories,
    filterAgeGroup,
    filterAllWeather,
    filterAccessible,
    filterFree,
    enabled: true
  });

  const isMapReady = !loading;

  // Sauvegarder les filtres dans localStorage
  useEffect(() => { localStorage.setItem('filterDate', filterDate); }, [filterDate]);
  useEffect(() => { localStorage.setItem('filterEndDate', filterEndDate); }, [filterEndDate]);
  useEffect(() => { localStorage.setItem('filterLocation', filterLocation); }, [filterLocation]);
  useEffect(() => { localStorage.setItem('communeName', communeName); }, [communeName]);
  useEffect(() => { localStorage.setItem('communeInseeCode', communeInseeCode); }, [communeInseeCode]);
  useEffect(() => { if (searchPosition) localStorage.setItem('searchPosition', JSON.stringify(searchPosition)); }, [searchPosition]);
  useEffect(() => { localStorage.setItem('filterDistance', filterDistance.toString()); }, [filterDistance]);
  useEffect(() => { localStorage.setItem('filterCategories', JSON.stringify(filterCategories)); }, [filterCategories]);
  useEffect(() => { localStorage.setItem('filterAgeGroup', filterAgeGroup); }, [filterAgeGroup]);
  useEffect(() => { localStorage.setItem('filterAllWeather', filterAllWeather.toString()); }, [filterAllWeather]);
  useEffect(() => { localStorage.setItem('filterAccessible', filterAccessible.toString()); }, [filterAccessible]);
  useEffect(() => { localStorage.setItem('filterFree', filterFree.toString()); }, [filterFree]);

  // Mettre à jour le cache de filterLocation à CHAQUE changement
  useEffect(() => {
    cachedFilterLocation.current = filterLocation;
  }, [filterLocation]);

  // Recentrage automatique de la carte quand searchPosition change
  // UNIQUEMENT si searchPosition a vraiment changé (pas juste réassigné à la même valeur)
  useEffect(() => {
    // Si pas de searchPosition, pas de recentrage
    if (!searchPosition) {
      console.log('[HomePage] ⏭️  Pas de searchPosition, pas de recentrage');
      lastSearchPositionRef.current = null;
      return;
    }
    
    // Si filterLocation est vide (lieu supprimé), pas de recentrage
    if (!filterLocation) {
      console.log('[HomePage] ⏭️  filterLocation vide, pas de recentrage');
      lastSearchPositionRef.current = null;
      return;
    }
    
    // Vérifier si searchPosition a vraiment changé
    const lastPos = lastSearchPositionRef.current;
    if (lastPos && 
        lastPos.latitude === searchPosition.latitude && 
        lastPos.longitude === searchPosition.longitude) {
      console.log('[HomePage] ⏭️  searchPosition identique, pas de recentrage');
      return;
    }
    
    // Recentrer automatiquement sur le nouveau lieu
    const newCenter = [searchPosition.latitude, searchPosition.longitude];
    const newZoom = 12;
    
    console.log('[HomePage] 🎯 Recentrage automatique:', {
      filterLocation,
      latitude: searchPosition.latitude,
      longitude: searchPosition.longitude,
      zoom: newZoom
    });
    
    // Sauvegarder la dernière position utilisée
    lastSearchPositionRef.current = searchPosition;
    
    setMapCenter(newCenter);
    setMapZoom(newZoom);
    setCurrentZoom(newZoom);
    
    // Sauvegarder dans localStorage
    localStorage.setItem('mapCenter', JSON.stringify(newCenter));
    localStorage.setItem('mapZoom', newZoom.toString());
    
    // Si la carte est déjà montée, la recentrer
    if (mapRef.current) {
      mapRef.current.setView(newCenter, newZoom);
    }
  }, [searchPosition, filterLocation]);

  useEffect(() => {
    if (categoriesFetchedRef.current) return;

    const fetchCategories = async () => {
      categoriesFetchedRef.current = true;
      
      const { data, error } = await supabase
        .from('categories')
        .select('id, name, icon, color_code')
        .eq('is_active', true)
        .order('name', { ascending: true });
      
      if (!error) {
        setCategories(data || []);
      }
    };
    
    fetchCategories();
  }, []);

  useEffect(() => {
    if (ageGroupsFetchedRef.current) return;

    const fetchAgeGroups = async () => {
      ageGroupsFetchedRef.current = true;
      
      const { data, error } = await supabase
        .from('age_groups')
        .select('id, name, icon, min_age, max_age')
        .order('min_age', { ascending: true });
      
      if (!error) {
        setAgeGroups(data || []);
      }
    };
    
    fetchAgeGroups();
  }, []);

  // Utiliser directement filteredEvents au lieu de refiltrer
  // const filteredEvents = useEventFilters(...); // À SUPPRIMER
  const filteredEvents = events; // Les événements sont déjà filtrés côté serveur

  // Marquer le filtrage comme terminé après le chargement des événements ET la disponibilité de nearbyPostalCodes
  useEffect(() => {
    // Si on charge, on n'est jamais prêt
    if (loading) {
      setIsFilteringComplete(false);
      return;
    }

    // Si on a une position de recherche
    if (searchPosition) {
      // On attend que nearbyPostalCodes soit calculé ET que loading soit terminé
      const isReady = nearbyPostalCodes.length > 0 && !loading;
      setIsFilteringComplete(isReady);
      
      if (isReady) {
        console.log('[HomePage] ✅ Filtrage terminé:', { 
          nearbyPostalCodesLength: nearbyPostalCodes.length,
          filteredEventsLength: filteredEvents.length
        });
      }
    } else {
      // Pas de position = on affiche directement le message de localisation
      setIsFilteringComplete(true);
    }
  }, [loading, searchPosition, nearbyPostalCodes.length, filteredEvents.length]);

  // Logger les conditions pour le debug
  // useEffect(() => {
  //   console.log('[Display Logic]', {
  //     loading,
  //     isMapReady,
  //     searchPosition: !!searchPosition,
  //     isFilteringComplete,
  //     filteredEventsLength: filteredEvents.length,
  //     nearbyPostalCodesLength: nearbyPostalCodes.length
  //   });
  // }, [loading, isMapReady, searchPosition, isFilteringComplete, filteredEvents.length, nearbyPostalCodes.length]);

  // Gestionnaire pour sauvegarder UNIQUEMENT le zoom de la carte
  const handleMapMove = ({ center, zoom }) => {
    setMapCenter(center);
    setMapZoom(zoom);
    setCurrentZoom(zoom);
    // Sauvegarder le centre ET le zoom
    localStorage.setItem('mapCenter', JSON.stringify(center));
    localStorage.setItem('mapZoom', zoom.toString());
  };

  // Fonction pour recentrer la carte sur searchPosition (uniquement sur clic bouton)
  const handleRecenterMap = () => {
    if (mapRef.current && searchPosition) {
      const newCenter = [searchPosition.latitude, searchPosition.longitude];
      const newZoom = 12;
      mapRef.current.setView(newCenter, newZoom);
      setMapCenter(newCenter);
      setMapZoom(newZoom);
      setCurrentZoom(newZoom);
      localStorage.setItem('mapCenter', JSON.stringify(newCenter));
      localStorage.setItem('mapZoom', newZoom.toString());
    }
  };

  // Callback pour recentrer depuis FilterPanel lors d'un changement de localisation
  // Conformément à filterpanel.csv : mapCenter est mis à jour MANUELLEMENT uniquement
  // ou sur action explicite de l'utilisateur (bouton recentrage, sélection commune)
  const handleLocationChangeFromFilter = () => {
    // Recentrer immédiatement après sélection manuelle d'une commune
    if (searchPosition && mapRef.current) {
      const newCenter = [searchPosition.latitude, searchPosition.longitude];
      const newZoom = 12;
      mapRef.current.setView(newCenter, newZoom);
      setMapCenter(newCenter);
      setMapZoom(newZoom);
      setCurrentZoom(newZoom);
      localStorage.setItem('mapCenter', JSON.stringify(newCenter));
      localStorage.setItem('mapZoom', newZoom.toString());
      console.log('[MapCenter] Recentrage sur sélection commune:', newCenter);
    }
  };

  // Fonction pour rechercher la commune à partir des coordonnées GPS du centre de la carte (mapCenter local)
  const handleSearchInArea = async () => {
    if (!mapRef.current) return;
    
    // Récupérer le centre actuel de la carte (là où l'utilisateur a déplacé la vue)
    const center = mapRef.current.getCenter();
    const latitude = center.lat;
    const longitude = center.lng;
    
    console.log('[SearchInArea] Recherche à partir du centre de la carte:', { latitude, longitude });
    
    try {
      // Utiliser la fonction check_point_in_commune pour trouver le `code_commune_insee` via les polygones géométriques
      const { data: codeInsee, error: rpcError } = await supabase.rpc('check_point_in_commune', {
        p_lat: latitude,
        p_lon: longitude
      });

      if (rpcError) {
        console.error('[SearchInArea] Erreur RPC check_point_in_commune:', rpcError);
        return;
      }

      if (!codeInsee) {
        console.log('[SearchInArea] Aucun code INSEE trouvé pour ces coordonnées');
        return;
      }

      console.log('[SearchInArea] Code INSEE trouvé via polygone:', codeInsee);

      // Récupérer les informations complètes de la commune depuis communes_light (par INSEE)
      const { data: communeData, error: communeError } = await supabase
        .from('communes_light')
        .select('latitude, longitude, nom_de_la_commune, code_postal, code_commune_insee')
        .eq('code_commune_insee', codeInsee)
        .limit(1)
        .single();

      if (communeError || !communeData) {
        console.error('[SearchInArea] Erreur lors de la récupération des données de la commune:', communeError);
        return;
      }

      console.log('[SearchInArea] Commune trouvée:', communeData);
      
      // Mettre à jour les états de localisation
      setFilterLocation(communeData.code_postal);
      setCommuneName(communeData.nom_de_la_commune);
      setCommuneInseeCode(communeData.code_commune_insee);
      
      // Sauvegarder dans localStorage
      localStorage.setItem('filterLocation', communeData.code_postal);
      localStorage.setItem('communeName', communeData.nom_de_la_commune);
      localStorage.setItem('communeInseeCode', communeData.code_commune_insee);
      
      // Mettre à jour la searchPosition avec les coordonnées de la commune (centroïde)
      const position = {
        latitude: parseFloat(communeData.latitude),
        longitude: parseFloat(communeData.longitude)
      };
      setSearchPosition(position);
      localStorage.setItem('searchPosition', JSON.stringify(position));
      localStorage.setItem('lastSearchTime', Date.now().toString());
      
      console.log('[SearchInArea] Position mise à jour:', position);
    } catch (err) {
      console.error('[SearchInArea] Erreur:', err);
    }
  };

  // Callback pour gérer la sélection de commune depuis NoLocationMessage
  const handleSelectCommuneFromNoLocation = async (commune) => {
    console.log('[NoLocationMessage] Commune sélectionnée:', commune);
    
    // Mettre à jour les états de localisation
    setFilterLocation(commune.codePostal);
    setCommuneName(commune.nom);
    setCommuneInseeCode(commune.code);
    
    // Sauvegarder dans localStorage
    localStorage.setItem('filterLocation', commune.codePostal);
    localStorage.setItem('communeName', commune.nom);
    localStorage.setItem('communeInseeCode', commune.code);
    
    // Si nous avons un centre de commune depuis l'API Géo
    if (commune.centre) {
      const position = {
        latitude: commune.centre.coordinates[1],
        longitude: commune.centre.coordinates[0]
      };
      setSearchPosition(position);
      localStorage.setItem('searchPosition', JSON.stringify(position));
      localStorage.setItem('lastSearchTime', Date.now().toString());
      
      // Recentrer la carte
      const newCenter = [position.latitude, position.longitude];
      const newZoom = 12;
      if (mapRef.current) {
        mapRef.current.setView(newCenter, newZoom);
      }
      setMapCenter(newCenter);
      setMapZoom(newZoom);
      setCurrentZoom(newZoom);
      localStorage.setItem('mapCenter', JSON.stringify(newCenter));
      localStorage.setItem('mapZoom', newZoom.toString());
      
      console.log('[NoLocationMessage] Position mise à jour:', position);
    } else {
      // Sinon, récupérer le centroïde depuis Supabase
      try {
        const { data, error } = await supabase
          .from('communes')
          .select('centroide')
          .eq('code', commune.code)
          .single();
        
        if (!error && data && data.centroide) {
          const position = {
            latitude: data.centroide.coordinates[1],
            longitude: data.centroide.coordinates[0]
          };
          setSearchPosition(position);
          localStorage.setItem('searchPosition', JSON.stringify(position));
          localStorage.setItem('lastSearchTime', Date.now().toString());
          
          // Recentrer la carte
          const newCenter = [position.latitude, position.longitude];
          const newZoom = 12;
          if (mapRef.current) {
            mapRef.current.setView(newCenter, newZoom);
          }
          setMapCenter(newCenter);
          setMapZoom(newZoom);
          setCurrentZoom(newZoom);
          localStorage.setItem('mapCenter', JSON.stringify(newCenter));
          localStorage.setItem('mapZoom', newZoom.toString());
          
          console.log('[NoLocationMessage] Position récupérée depuis Supabase:', position);
        }
      } catch (err) {
        console.error('[NoLocationMessage] Erreur lors de la récupération du centroïde:', err);
      }
    }
  };

  // L'initialisation de mapCenter se fait uniquement dans le useState initial
  // Les mises à jour se font via actions manuelles : drag carte, bouton recentrage, sélection commune

  // Récupérer le paramètre tile depuis la base de données
  useEffect(() => {
    const fetchTileParameter = async () => {
      const { data, error } = await supabase
        .from('parameters')
        .select('value')
        .eq('parameter', 'tile')
        .single();
      
      if (!error && data) {
        setTileProvider(data.value);
      }
    };
    
    fetchTileParameter();
  }, []);

  // Déterminer ce qu'il faut afficher - logique simplifiée
  const shouldShowLoading = loading || locationLoading || (searchPosition && nearbyPostalCodes.length === 0) || !isFilteringComplete;
  const shouldShowNoLocation = !loading && !locationLoading && !searchPosition && isFilteringComplete;
  const shouldShowNoEvents = !loading && !locationLoading && searchPosition && isFilteringComplete && nearbyPostalCodes.length > 0 && filteredEvents.length === 0;
  const shouldShowMap = !loading && !locationLoading && isFilteringComplete && searchPosition && nearbyPostalCodes.length > 0;

  // Log détaillé de l'état d'affichage
  // useEffect(() => {
  //   console.log('[Display State]', {
  //     loading,
  //     searchPosition: !!searchPosition,
  //     nearbyPostalCodesLength: nearbyPostalCodes.length,
  //     isFilteringComplete,
  //     filteredEventsLength: filteredEvents.length,
  //     shouldShowLoading,
  //     shouldShowNoLocation,
  //     shouldShowNoEvents,
  //     shouldShowMap,
  //     currentDisplay: shouldShowLoading ? 'LOADING' : 
  //                    shouldShowNoLocation ? 'NO_LOCATION' : 
  //                    shouldShowNoEvents ? 'NO_EVENTS' : 
  //                    shouldShowMap ? 'MAP_WITH_EVENTS' : 'UNKNOWN'
  //   });
  // }, [shouldShowLoading, shouldShowNoLocation, shouldShowNoEvents, shouldShowMap, loading, searchPosition, nearbyPostalCodes.length, isFilteringComplete, filteredEvents.length]);

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <style>{`
        /* Empêcher le scroll sur toute la page */
        html, body {
          overflow: hidden !important;
          height: 100%;
          width: 100%;
          position: fixed;
        }
        
        /* Styles personnalisés pour les contrôles Leaflet avec la couleur Maploop */
        .leaflet-control-zoom a,
        .leaflet-control-layers-toggle {
          background-color: white !important;
          color: #2894D8 !important;
          border: 0px solid #2894D8 !important;
        }
        
        .leaflet-control-zoom a:hover,
        .leaflet-control-layers-toggle:hover {
          background-color: #2894D8 !important;
          color: white !important;
        }
        
        .leaflet-control-layers {
          border: 0px solid #2894D8 !important;
        }
        
        .leaflet-control-layers-expanded {
          background-color: white !important;
        }
        
        /* Ajuster le z-index pour éviter la superposition avec le filtre */
        .leaflet-top.leaflet-right {
          z-index: 400 !important;
        }
        
        .leaflet-top.leaflet-left {
          z-index: 400 !important;
        }
        
        /* Style pour le titre des calques */
        .leaflet-control-layers-base label,
        .leaflet-control-layers-overlays label {
          color: #374151;
          font-weight: 500;
        }
        
        /* Style pour les radio buttons */
        .leaflet-control-layers-selector {
          accent-color: #2894D8;
        }
        
        /* Agrandir le bouton de fermeture du popup */
        .leaflet-popup-close-button {
          width: 24px !important;
          height: 24px !important;
          font-size: 28px !important;
          line-height: 28px !important;
          padding: 0 !important;
          color: #374151 !important;
          font-weight: bold !important;
        }
        
        .leaflet-popup-close-button:hover {
          color: #2894D8 !important;
        }
        
        /* Réduire la largeur du popup */
        .leaflet-popup-content-wrapper {
          max-width: 250px !important;
        }
        
        .leaflet-popup-content {
          margin: 12px !important;
          max-width: 250px !important;
        }
        
        /* Hauteur de la carte adaptée au mobile */
        .map-container {
          height: calc(100dvh - 72px - 52px);
          position: relative;
          overflow: hidden;
        }
        
        @supports not (height: 100dvh) {
          .map-container {
            height: calc(100vh - 72px - 52px);
            overflow: hidden;
          }
        }
        
        /* Améliorer le rendu des marqueurs sur iOS */
        .custom-marker-round {
          background: transparent !important;
          border: none !important;
        }
        
        .custom-marker-round svg {
          overflow: visible;
          display: block;
        }
        
        /* Animation pulse pour les marqueurs */
        @keyframes pulse-marker {
          0%, 100% { 
            transform: scale(1);
            opacity: 1;
          }
          50% { 
            transform: scale(1.1);
            opacity: 0.9;
          }
        }
        
        /* Animation pulse pour le marqueur utilisateur */
        @keyframes pulse-user {
          0%, 100% { 
            opacity: 0.3;
            transform: scale(1);
          }
          50% { 
            opacity: 0.1;
            transform: scale(2);
          }
        }
      `}</style>

      {/* Panneau de filtres */}
      <FilterPanel
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        filterDate={filterDate}
        setFilterDate={setFilterDate}
        filterEndDate={filterEndDate}
        setFilterEndDate={setFilterEndDate}
        filterLocation={filterLocation}
        setFilterLocation={setFilterLocation}
        communeName={communeName}
        setCommuneName={setCommuneName}
        communeInseeCode={communeInseeCode}
        setCommuneInseeCode={setCommuneInseeCode}
        searchPosition={searchPosition}
        setSearchPosition={setSearchPosition}
        filterDistance={filterDistance}
        setFilterDistance={setFilterDistance}
        nearbyPostalCodes={nearbyPostalCodes}
        setNearbyPostalCodes={setNearbyPostalCodes}
        setNearbyCommuneInsee={setNearbyCommuneInsee}
        filterCategories={filterCategories}
        setFilterCategories={setFilterCategories}
        categories={categories}
        eventsCount={events.length}
        filterAgeGroup={filterAgeGroup}
        setFilterAgeGroup={setFilterAgeGroup}
        ageGroups={ageGroups}
        filterAllWeather={filterAllWeather}
        setFilterAllWeather={setFilterAllWeather}
        filterAccessible={filterAccessible}
        setFilterAccessible={setFilterAccessible}
        filterFree={filterFree}
        setFilterFree={setFilterFree}
        mapCenter={mapCenter}
        setMapCenter={setMapCenter}
        mapZoom={mapZoom}
        setMapZoom={setMapZoom}
        onLocalGPSPositionUpdate={handleLocalGPSPositionUpdate}
        onLocationChange={handleLocationChangeFromFilter}
        onLocationLoadingChange={setLocationLoading}
      />

      {/* Carte - plein écran */}
      <div className="fixed map-container mt-[72px] overflow-hidden">
        {shouldShowLoading ? (
          <LoadingSpinner />
        ) : shouldShowNoLocation ? (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[600] w-full max-w-md px-4">
            <NoLocationMessage 
              isFloating={true} 
              onSelectCommune={handleSelectCommuneFromNoLocation}
            />
          </div>
        ) : shouldShowNoEvents ? (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[600] w-full max-w-md px-4">
            <NoEventsMessage 
              isFloating={true} 
              cityName={communeName}
              postalCode={filterLocation}
            />
          </div>
        ) : shouldShowMap ? (
          <>
            <MapContainer 
              center={mapCenter} 
              zoom={mapZoom} 
              style={{ height: '100%', width: '100%' }}
              ref={mapRef}
              //Contrôle de l'affichage des contributeurs (attribution)
              attributionControl={true}
            >
              <MapEventsHandler onMapMove={handleMapMove} />
              <LayersControl position="topright">
                <LayersControl.BaseLayer checked={tileProvider === 'osm'} name={t('map.standard')}>
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name={t('map.stadiaSmooth')}>
                  <TileLayer
                    attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
                    url={`https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png?api_key=${import.meta.env.VITE_STADIAMAPS_KEY}`}
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name={t('map.stadiaSmoothDark')}>
                  <TileLayer
                    attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
                    url={`https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png?api_key=${import.meta.env.VITE_STADIAMAPS_KEY}`}
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer  checked={tileProvider === 'stradiamaps'} name={t('map.stadiaOutdoors')}>
                  <TileLayer
                    attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
                    url={`https://tiles.stadiamaps.com/tiles/outdoors/{z}/{x}/{y}{r}.png?api_key=${import.meta.env.VITE_STADIAMAPS_KEY}`}
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name={t('map.stadiaOSMBright')}>
                  <TileLayer
                    attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors'
                    url={`https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png?api_key=${import.meta.env.VITE_STADIAMAPS_KEY}`}
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name={t('map.satellite')}>
                  <TileLayer
                    attribution='&copy; <a href="https://www.esri.com">Esri</a>'
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name={t('map.terrain')}>
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                  />
                </LayersControl.BaseLayer>
              </LayersControl>
              
              {/* Marqueurs pour les événements filtrés */}
              {filteredEvents.map(event => {
                if (!event.latitude || !event.longitude) return null;
                
                // Convertir les coordonnées (si elles sont en format texte avec virgule)
                const lat = typeof event.latitude === 'string' 
                  ? parseFloat(event.latitude.replace(',', '.'))
                  : parseFloat(event.latitude);
                const lng = typeof event.longitude === 'string'
                  ? parseFloat(event.longitude.replace(',', '.'))
                  : parseFloat(event.longitude);
                
                // Vérifier que les coordonnées sont valides
                if (isNaN(lat) || isNaN(lng)) return null;
                
                // Vérifier si l'événement est actif aujourd'hui
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const eventStartDate = new Date(event.start_date);
                eventStartDate.setHours(0, 0, 0, 0);
                const eventEndDate = event.end_date ? new Date(event.end_date) : eventStartDate;
                eventEndDate.setHours(23, 59, 59, 999);
                const isToday = today.getTime() >= eventStartDate.getTime() && today.getTime() <= eventEndDate.getTime();
                
                // Afficher le titre si le zoom est >= 13
                const showTitle = currentZoom >= 15;
                
                // Créer le marker personnalisé avec la couleur de la catégorie
                const customIcon = createCustomMarker(
                  event.category?.color_code, 
                  event.category?.name || 'Culture',
                  isToday,
                  event.boost,
                  event.title,
                  showTitle
                );
                
                return (
                  <Marker 
                    key={event.id} 
                    position={[lat, lng]}
                    icon={customIcon}
                    zIndexOffset={event.boost ? 1000 : isToday ? 500 : 0}
                    eventHandlers={{
                      click: () => navigate(`/event/${event.id}`)
                    }}
                  >
                  </Marker>
                );
              })}

              {/* Cercle de périmètre du filtre distance - centré sur searchPosition */}
              {searchPosition && filterDistance > 0 && (
                <Circle
                  center={[searchPosition.latitude, searchPosition.longitude]}
                  radius={filterDistance * 1000}
                  pathOptions={{
                    color: '#2894D8',
                    fillColor: '#2894D8',
                    fillOpacity: 0.00,
                    weight: 1
                  }}
                />
              )}

              {/* Marqueur de position GPS de l'utilisateur avec pulsation */}
              {localGPSPosition && (
                <Marker
                  position={[localGPSPosition.latitude, localGPSPosition.longitude]}
                  icon={createUserPositionIcon()}
                  zIndexOffset={2000}
                >
                  <Popup>
                    <div>
                      <p className="font-medium text-sm">{t('map.yourPosition')}</p>
                    </div>
                  </Popup>
                </Marker>
              )}
            </MapContainer>
            
            {/* Bouton pour rechercher dans cette zone */}
            <button
              onClick={handleSearchInArea}
              className="absolute bottom-36 right-4 z-[500] bg-white text-[#2894D8] p-3 rounded-full shadow-lg hover:bg-gray-50 hover:shadow-xl transition-all duration-200 border border-gray-200"
              title={t('filters.searchInArea')}
            >
              <SearchIcon className="h-6 w-6" strokeWidth="2" />
            </button>
            
            {/* Bouton pour recentrer la carte */}
            {searchPosition && (
              <button
                onClick={handleRecenterMap}
                className="absolute bottom-20 right-4 z-[500] bg-white text-[#2894D8] p-3 rounded-full shadow-lg hover:bg-gray-50 hover:shadow-xl transition-all duration-200 border border-gray-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default HomePage;